import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type TableStatus = 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'DIRTY';
export type TableShape = 'rectangle' | 'circle' | 'square';

export interface DiningTable {
  id: string;
  tenant_id: string;
  location_id: string;
  table_number: string;
  seating_capacity: number;
  floor: string;
  section?: string | null;
  shape?: TableShape;
  position_x?: number | null;
  position_y?: number | null;
  status: TableStatus;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// NOTE: the /dining-tables contract runs a strict validation pipe (whitelist +
// forbidNonWhitelisted), so sending any field the API doesn't accept makes the
// whole create/update request fail. These payload types therefore expose ONLY
// the supported fields, and the helpers below defensively strip anything else
// before it reaches the wire.
//
// PATCH/PUT /dining-tables accepts table_number, seating_capacity, floor, shape,
// `section` and `is_active` (GitHub issue #279 — the Edit modal's Section field
// and Active toggle must persist). `position_x`/`position_y` remain non-request
// fields. Create accepts location_id, table_number, seating_capacity, floor and
// shape.
export interface CreateTablePayload {
  table_number: string;
  seating_capacity?: number;
  floor?: string;
  shape?: TableShape;
  location_id: string;
}

export interface UpdateTablePayload {
  table_number?: string;
  seating_capacity?: number;
  floor?: string;
  shape?: TableShape;
  section?: string | null;
  is_active?: boolean;
}

const CREATE_FIELDS = [
  'location_id',
  'table_number',
  'seating_capacity',
  'floor',
  'shape',
] as const;

const UPDATE_FIELDS = [
  'table_number',
  'seating_capacity',
  'floor',
  'shape',
  'section',
  'is_active',
] as const;

/** Keep only the API-supported fields; drop everything else (and undefined). */
function pickFields<T extends object>(obj: T, keys: readonly string[]): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    const value = (obj as Record<string, unknown>)[key];
    if (value !== undefined) out[key] = value;
  }
  return out as Partial<T>;
}

export interface TableListResponse {
  data: DiningTable[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// Location-wide KPI aggregate (GitHub issue #278). Computed server-side across
// every table at the location so the KPI cards stay constant across pages,
// instead of being reduced from the current 20-row page slice.
export interface TableStats {
  total_tables: number;
  total_capacity: number;
  active_tables: number;
  sections: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

// ─── Service ────────────────────────────────────────────────────────────────

export const tableService = {
  list(params?: {
    location_id?: string;
    page?: number;
    limit?: number;
    status?: TableStatus;
  }): Promise<TableListResponse> {
    return apiClientPaginated<DiningTable[]>(
      `/dining-tables${buildQuery({ page: 1, limit: 200, ...params })}`,
    );
  },

  getStats(params?: { location_id?: string; status?: TableStatus }): Promise<TableStats> {
    return apiClient<TableStats>(`/dining-tables/stats${buildQuery({ ...params })}`);
  },

  getById(id: string): Promise<DiningTable> {
    return apiClient<DiningTable>(`/dining-tables/${id}`);
  },

  create(payload: CreateTablePayload): Promise<DiningTable> {
    return apiClient<DiningTable>('/dining-tables', {
      method: 'POST',
      body: pickFields(payload, CREATE_FIELDS),
    });
  },

  update(id: string, payload: UpdateTablePayload): Promise<DiningTable> {
    return apiClient<DiningTable>(`/dining-tables/${id}`, {
      method: 'PUT',
      body: pickFields(payload, UPDATE_FIELDS),
    });
  },

  delete(id: string): Promise<void> {
    return apiClient<void>(`/dining-tables/${id}`, { method: 'DELETE' });
  },

  updateStatus(id: string, status: TableStatus): Promise<DiningTable> {
    return apiClient<DiningTable>(`/dining-tables/${id}/status`, {
      method: 'PUT',
      body: { status },
    });
  },
};
